/**
 * **The address contract of the interview screens, and the whole of it** — the shape
 * `pages/Vacancies/vacancyUrls.ts` established for §6.8/§6.9 and `candidateFilters.ts` for §6.3.
 *
 * `DESIGN.md` §6.6 (filling in) and §6.7 (submitted) are **one address**, and which of them a reader
 * gets is decided by the payload rather than by the URL: a readable interview with `submitted_at ===
 * null` is §6.6, a readable one with it set is §6.7, and a blinded one is the state TASK-080 designs.
 * There is no `/interviews/:id/edit`, and there must not be — a second address would be a second place
 * to reason about whether feedback is still writable, which ADR 0008 settles once.
 *
 * ```
 * /interviews/42
 * ```
 *
 * ⚠️ **This is the one module that spells the address.** `routes.tsx` matches on `INTERVIEW_PATH`
 * (TASK-080), and §6.2 Section 1's outstanding-feedback rows and §6.5's interview rows link through
 * `interviewUrl` (TASK-087) — none of them has to know that the parameter is `:interviewId`.
 * `interviews.test.tsx` fails any other module under `src/` that spells `/interviews` as a URL literal,
 * with the exemption list asserted as an equality so a second owner cannot join it quietly.
 *
 * ⚠️ **There is deliberately no id parser here, and TASK-080 settled where it went.** A hand-typed
 * `/interviews/ada` must make **no request at all** — `fillPath` does not encode its parameters
 * (Backlog, demonstrated by `security-auditor`), and `/api/v1/interviews/NaN/` would answer 404, match
 * `CANDIDATE_SCOPED_RECORD` and render as *"you do not have access"*, which is §6.3's named failure.
 * Until TASK-080 this file said the `/^\d+$/` check *"belongs at the route that reads the parameter"*
 * and would move here the moment it had a caller; it has one now, and it stayed at the route —
 * `pages/Interview/index.tsx`, above every hook that could make a request, which is the property being
 * bought and the one an exported helper cannot give. The two record screens that already do this
 * inline it in exactly the same line (`PersonCard/index.tsx`, `QuestionBank/index.tsx`), so a third
 * spelling of a one-line regex here would be indirection rather than ownership: what this module owns
 * is the **address**, and `/^\d+$/` is not part of it.
 */

/**
 * The path parameter, as data: `routes.tsx` matches on it and the screen reads it by this name, so a
 * rename is one edit and cannot half-happen (the screen would silently see `undefined` and answer
 * *"There is nothing at this address."*, which is exactly the failure a shared constant avoids).
 */
export const INTERVIEW_ID_PARAM = 'interviewId';

/** The address, as react-router matches it and as `interviewUrl` fills it. */
export const INTERVIEW_PATH = `/interviews/:${INTERVIEW_ID_PARAM}` as const;

/**
 * One interview's address — **the way another screen links to it**.
 *
 * The two deferrals the Backlog records by name are its callers (TASK-087): §6.2 Section 1's
 * outstanding-feedback rows and §6.5's interview rows. **Neither renders a link yet** — until TASK-080
 * because the address was not mounted at all, and since then because landing the two links is its own
 * task, which decides what each kind of row points at.
 *
 * It takes a **number**, which is the interview's own id off a payload, so nothing user-typed reaches it
 * — the address bar is parsed at the route, not here.
 */
export function interviewUrl(interviewId: number): string {
  return INTERVIEW_PATH.replace(`:${INTERVIEW_ID_PARAM}`, String(interviewId));
}
